// iter78t — Standalone Work Order detail page (deep-linkable, printable, browser-back-friendly).
// Mirrors the content of the in-page dialog from ProductionQueuePage so the same
// WO data is visible whether you click WO# (dialog) or paste a /work-orders/:id URL.

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Printer, ExternalLink, Tag, Factory, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import WorkOrderLabelDialog from '../components/WorkOrderLabelDialog';
import { AssemblyBuildsCard } from '../components/AssemblyBuildsCard';

const STATUS_TONE = {
  Draft: 'bg-slate-100 text-slate-700 border-slate-300',
  Released: 'bg-blue-50 text-blue-700 border-blue-300',
  InProgress: 'bg-amber-50 text-amber-800 border-amber-300',
  Paused: 'bg-orange-50 text-orange-700 border-orange-300',
  Completed: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  Closed: 'bg-slate-200 text-slate-600 border-slate-400',
};

const Row = ({ label, value, mono, testId }) => (
  <div className="flex items-baseline justify-between py-2 border-b border-slate-100 last:border-0">
    <span className="text-[11px] uppercase tracking-widest text-slate-500">{label}</span>
    <span className={`text-sm ${mono ? 'font-mono' : 'font-medium'} text-slate-900`} data-testid={testId}>{value ?? '—'}</span>
  </div>
);

const WorkOrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [wo, setWo] = useState(null);
  const [bom, setBom] = useState(null);
  const [pt, setPt] = useState(null);
  const [units, setUnits] = useState([]);
  const [time, setTime] = useState([]);
  const [loading, setLoading] = useState(true);
  const [labelOpen, setLabelOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const w = await api().get(`/work-orders/${id}`);
        if (!alive) return;
        setWo(w.data);
        const [bRes, ptRes, fuRes, teRes] = await Promise.all([
          w.data.bom_id ? api().get(`/boms/${w.data.bom_id}`).catch(() => null) : Promise.resolve(null),
          w.data.product_type_id ? api().get(`/product-types/${w.data.product_type_id}`).catch(() => null) : Promise.resolve(null),
          api().get(`/finished-units?work_order_id=${id}`).catch(() => ({ data: [] })),
          api().get(`/time-entries?work_order_id=${id}`).catch(() => ({ data: [] })),
        ]);
        if (!alive) return;
        setBom(bRes?.data || null);
        setPt(ptRes?.data || null);
        setUnits(fuRes?.data || []);
        setTime(teRes?.data || []);
      } catch (e) {
        // iter78v — alt-env redirect for stale label scans
        if (e?.response?.status === 404) {
          const { tryAltEnvRedirect } = await import('../utils/altEnvRedirect');
          const redirected = await tryAltEnvRedirect(api);
          if (redirected) return;
        }
        toast.error(e.response?.data?.detail || 'Work order not found');
        navigate('/work-orders');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id, api, navigate]);

  if (loading) return <div className="p-8 text-center text-slate-500" data-testid="wo-detail-loading">Loading work order…</div>;
  if (!wo) return null;

  const woNumber = wo.work_order_number || `WO-${wo.id.slice(0, 8).toUpperCase()}`;
  const tone = STATUS_TONE[wo.status] || STATUS_TONE.Draft;
  const totalSeconds = (time || []).reduce((s, t) => s + (t.duration_seconds || 0), 0);
  const hrs = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);

  return (
    <div className="space-y-4" data-testid="wo-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/work-orders')} className="rounded-sm" data-testid="wo-detail-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Production Queue
          </Button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-slate-900 font-mono">{woNumber}</h1>
              <Badge variant="outline" className={tone}>{wo.status}</Badge>
              {wo.approval_status && (
                <Badge variant="outline" className={wo.approval_status === 'approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : 'bg-amber-50 text-amber-800 border-amber-300'}>
                  {wo.approval_status}
                </Badge>
              )}
            </div>
            <p className="text-sm text-slate-500 mt-1">{pt?.product_type_name || 'Unknown product'} · {bom?.bom_variant_code || bom?.bom_name || '—'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setLabelOpen(true)} className="rounded-sm" data-testid="wo-detail-print-label">
            <Tag className="w-4 h-4 mr-1" /> Print Label
          </Button>
          <Button variant="outline" onClick={() => window.print()} className="rounded-sm" data-testid="wo-detail-print-page">
            <Printer className="w-4 h-4 mr-1" /> Print Page
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="industrial-card lg:col-span-2">
          <CardHeader className="border-b border-slate-100 pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Factory className="w-4 h-4 text-orange-600" /> Work Order</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <Row label="Work Order #" value={woNumber} mono testId="wo-detail-num" />
            <Row label="Queue Type" value={wo.queue_type === 'finished_production' ? 'Final Product' : (wo.queue_type === 'part_assembly' ? 'Part Assembly' : wo.queue_type)} testId="wo-detail-queue" />
            <Row label="Target Quantity" value={wo.target_quantity} mono />
            <Row label="Completed Quantity" value={wo.completed_quantity || 0} mono />
            <Row label="Priority" value={wo.priority} mono />
            <Row label="Created" value={wo.created_date ? new Date(wo.created_date).toLocaleString() : '—'} testId="wo-detail-created" />
            {wo.released_date && <Row label="Released" value={new Date(wo.released_date).toLocaleString()} />}
            {wo.completed_date && <Row label="Completed" value={new Date(wo.completed_date).toLocaleString()} />}
            {wo.approved_by_name && <Row label="Approved By" value={`${wo.approved_by_name}${wo.approved_at ? ` · ${new Date(wo.approved_at).toLocaleString()}` : ''}`} />}
            {wo.notes && <div className="mt-3 p-3 rounded-sm bg-slate-50 text-sm text-slate-700"><span className="text-[10px] uppercase tracking-widest text-slate-500">Notes</span><p className="mt-1">{wo.notes}</p></div>}
          </CardContent>
        </Card>

        <Card className="industrial-card">
          <CardHeader className="border-b border-slate-100 pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4 text-orange-600" /> Time Log</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="text-center pb-3 border-b border-slate-100">
              <p className="text-[10px] uppercase tracking-widest text-slate-500">Total Logged</p>
              <p className="text-3xl font-mono font-bold text-slate-900 mt-1">{hrs}h {mins}m</p>
              <p className="text-xs text-slate-500 mt-1">{time.length} time {time.length === 1 ? 'entry' : 'entries'}</p>
            </div>
            <div className="max-h-64 overflow-y-auto mt-3 space-y-1">
              {time.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No time logged</p>}
              {time.slice(0, 15).map((t, idx) => (
                <div key={idx} className="text-xs flex items-center justify-between bg-slate-50 px-2 py-1 rounded-sm">
                  <span className="font-medium text-slate-700">{t.user_name || '—'}</span>
                  <span className="font-mono text-slate-500">
                    {Math.floor((t.duration_seconds || 0) / 3600)}h {Math.floor(((t.duration_seconds || 0) % 3600) / 60)}m
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <AssemblyBuildsCard wo={wo} />

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3">
          <CardTitle className="text-base flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Minted Serials ({units.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          {units.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">No serials minted yet. Click Complete in the production queue.</p>
          ) : (
            <div className="space-y-2">
              {units.map(u => (
                <div key={u.id} className="flex items-center justify-between p-2 rounded-sm bg-slate-50" data-testid={`wo-detail-serial-${u.id}`}>
                  <div>
                    <p className="font-mono font-bold text-slate-900">{u.serial_number}</p>
                    <p className="text-[11px] text-slate-500">{u.build_date ? new Date(u.build_date).toLocaleString() : '—'} · by {u.built_by_name || '—'}</p>
                  </div>
                  <Link to={`/finished-units/${u.id}`} className="text-orange-600 hover:underline text-xs flex items-center gap-1">
                    Open <ExternalLink className="w-3 h-3" />
                  </Link>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <WorkOrderLabelDialog
        open={labelOpen}
        onOpenChange={setLabelOpen}
        wo={wo}
        unit={units[0] || null}
        bom={bom}
        pt={pt}
      />
    </div>
  );
};

export default WorkOrderDetailPage;
